import Vue from 'vue';
import axios from 'axios';
import {ERROR_CODE, ERROR_CODE_NAME, TIME} from '@/const';
import requestMiddleware from '../requestMiddleware';
import {apiKeyErrorEvent} from '@/utils/helpers';
import {NO_API_KEY_URLS} from '@/urls';
import siteTracker from '@/components/base-components/Trackers/site24x7';
import {
  API_BASE_URLS,
  getApiUrl,
  hasReserveUrl,
  switchUrl,
} from '@/api/apiUrlController';

const DEFAULT_CONFIG = {
  retryCount: 1,
};
const DEFAULT_REQUEST_CONFIG = {
  [ERROR_CODE.THROTTLING]: {
    condition: () => true,
  },
  [ERROR_CODE_NAME.NETWORK]: {
    // show error reload screen just if request crushed when user onLine
    condition: () => navigator.onLine,
  },
  [ERROR_CODE_NAME.TIMEOUT]: {
    condition: () => true,
  },
};

const ERRORS_NAMES_TO_SWITCH_URL = [ERROR_CODE_NAME.TIMEOUT, ERROR_CODE_NAME.NETWORK];

const trackTime = (config) => {
  config.metadata.endTime = new Date().getTime();
  config.metadata.duration = config.metadata.endTime - config.metadata.startTime;
  return config;
};

const isCrashedErrorCode = (err) => {
  const customConfig = err?.config?.customConfig;
  const status = err.response?.status;

  return customConfig?.crashCodes?.some((codeReg) => {
    const reg = new RegExp(codeReg);
    return reg.test(status);
  });
};
const isAllowErrorCode = (err) => {
  return err.config?.customConfig?.allowedCodes?.includes(err.response?.status);
};

axios.interceptors.request.use(async (requestConfig) => {
  requestConfig.metadata = {
    startTime: new Date().getTime(),
  };
  return await requestMiddleware.checkApiKey(requestConfig);
});
axios.interceptors.response.use((response) => {
  trackTime(response.config);
  if (response.status !== 200) {
    return Promise.reject(response.data);
  }
  return response;
}, async (err) => {
  if (!API_BASE_URLS.includes(err.config?.baseURL)) {
    return Promise.reject(err);
  }

  err.code !== ERROR_CODE_NAME.ABORT && trackTime(err.config);
  if (
    err.code === ERROR_CODE_NAME.ABORT ||
      err.response?.status === ERROR_CODE.THROTTLING
  ) {
    return Promise.reject(err);
  }

  if (
    ERRORS_NAMES_TO_SWITCH_URL.includes(err.code) &&
    hasReserveUrl(err) &&
    navigator.onLine
  ) {
    return switchUrl(err, axios);
  }

  const {
    config,
    response,
  } = err;
  const apiKeyError = Boolean(response?.data?.error?.errors?.api_key);
  const hasRequestCustomConfig = Object.keys(config.customConfig)
      .filter((key) => key !== 'initiatorPage').length;
  const isKeyErrorForNoConfig = apiKeyError && !hasRequestCustomConfig;
  const delayRetryRequest = new Promise((resolve) => {
    setTimeout(resolve, config.customConfig?.retryDelay || TIME.SECOND);
  });

  if (!isAllowErrorCode(err) && err?.config?.customConfig?.retryCount || isKeyErrorForNoConfig) {
    if (apiKeyError) {
      Vue.$storage.pull('api_key');
    }
    if (apiKeyError) {
      await requestMiddleware.resetStore();
    }
    return delayRetryRequest.then(
        async () => {
          let newConfig = config;
          if (!Vue.$storage.get('api_key')) {
            await requestMiddleware.getApiKey();
            newConfig = requestMiddleware.updateApiKey(newConfig);
          }

          if (isKeyErrorForNoConfig) {
            newConfig.customConfig = {...DEFAULT_CONFIG};
          }

          newConfig.customConfig.retryCount = newConfig.customConfig.retryCount - 1;
          return axios({
            ...newConfig,
          });
        },
    );
  } else {
    if (err.code === ERROR_CODE.UNAUTHENTICATED) {
      await requestMiddleware.resetStore();
    }
    return Promise.reject(err);
  }
});

/**
 * Base request method
 * @param {string} url
 * @param {object} params
 * @param {string} method
 * @param {string} baseUrl
 * @param {object} token
 * @param {object} config
 * @param {[function]} tracking
 * @return {Promise<any>}
 */
async function request({
  url,
  params = {},
  method = 'post',
  baseUrl = getApiUrl(),
  token,
  config,
  tracking = [],
}) {
  const {
    params: preparedParams,
    url: preparedUrl,
  } = requestMiddleware.prepareParams({
    params,
    method,
    url,
  });
  return axios({
    url: preparedUrl,
    method: method,
    baseURL: baseUrl,
    timeout: 30000,
    data: preparedParams,
    signal: token,
    customConfig: {...(config || {}), initiatorPage: location.pathname},
  }).then((res) => {
    tracking.forEach((method) => method(res));
    !NO_API_KEY_URLS.includes(url) && apiKeyErrorEvent(false, preparedUrl);
    return res.data;
  }).catch((err) => {
    if (
      err.code === ERROR_CODE_NAME.ABORT ||
      !API_BASE_URLS.includes(err.config?.baseURL)
    ) {
      return Promise.reject(err);
    }

    tracking.forEach((method) => method(err)); // Analytics from the selected place and request
    siteTracker.sendRequestError(err);

    const status = err.response?.status;
    const defaultConfig = DEFAULT_REQUEST_CONFIG[status] || DEFAULT_REQUEST_CONFIG[err.code];
    const defaultConfigCondition = defaultConfig?.condition();
    const crashByDefaultErrors = err.config?.customConfig?.crashByDefaultErrors ?? true;

    const needErrorScreen = [
      defaultConfigCondition && crashByDefaultErrors,
      err.config?.customConfig?.globalError && isCrashedErrorCode(err) && !isAllowErrorCode(err),
      err.response?.data?.error?.errors?.api_key,
    ].some(Boolean);
    
    if (needErrorScreen) {
      apiKeyErrorEvent(true, preparedUrl);
    }
    const data = {
      ...(err.response?.data || {status: err.code, message: err.message}),
      config: {
        ...(err.config?.customConfig || {}),
        requestUrl: preparedUrl,
        preparedParams,
        method,
      },
    };
    return Promise.reject(data);
  });
}

export default requestMiddleware.checkDuplication(request);
